import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Truck, CheckCircle2, PackageCheck, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { usePartner } from "./PartnerLayout";

const FLOW = [
  { k: "confirmed", label: "Confirmer la commande", icon: CheckCircle2 },
  { k: "shipped", label: "Marquer expédiée", icon: Truck },
  { k: "delivered", label: "Marquer livrée", icon: PackageCheck },
];
const LABEL = { confirmed: "Confirmée", shipped: "Expédiée", delivered: "Livrée" };

/** Fournisseur : bons de commande adressés, confirmation / expédition / livraison, dépôt de facture. */
export default function PartnerOrders() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const me = usePartner();
  const [orders, setOrders] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ tracking: "", comment: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/partner/orders").then((r) => { setOrders(r.data); if (sel) setSel(r.data.find((o) => o.po_id === sel.po_id) || null); }).catch((e) => toast.error(e.response?.data?.detail || e.message)), [api, sel]);
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const next = sel ? FLOW[sel.supplier_status ? FLOW.findIndex((f) => f.k === sel.supplier_status) + 1 : 0] : null;

  const advance = async () => {
    try {
      await api.put(`/partner/orders/${sel.po_id}/supplier-status`, { status: next.k, ...form });
      toast.success(`Commande ${LABEL[next.k].toLowerCase()}`);
      setForm({ tracking: "", comment: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const uploadInvoice = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/partner/orders/${sel.po_id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Facture déposée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!orders) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;

  return (
    <div data-testid="partner-orders-page">
      <h1 className="text-2xl font-black text-white mb-1">Commandes</h1>
      <p className="text-xs text-slate-400 mb-6">Bons de commande qui vous sont adressés par {me.societe_name}.</p>
      {orders.length === 0 ? <div className="text-sm text-slate-500 italic border border-dashed border-white/10 rounded-xl p-8 text-center">Aucune commande pour le moment.</div> : (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-5 space-y-2">
            {orders.map((o) => (
              <button key={o.po_id} onClick={() => setSel(o)} data-testid={"partner-order-" + o.po_id}
                className={"w-full text-left rounded-xl border px-4 py-3 bg-white/[0.03] " + (sel?.po_id === o.po_id ? "border-[var(--brand-primary,#f7c935)]" : "border-white/10 hover:border-white/25")}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono text-slate-300">{o.po_id}</span>
                  <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (o.supplier_status === "delivered" ? "bg-emerald-500/15 text-emerald-400" : o.supplier_status ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/15 text-amber-400")} data-testid={"partner-order-status-" + o.po_id}>{LABEL[o.supplier_status] || "À confirmer"}</span>
                </div>
                <div className="text-sm text-white font-bold mt-1">{(o.lines || []).length} ligne{(o.lines || []).length > 1 ? "s" : ""} · {o.total_ht_eur?.toLocaleString("fr-FR")} € HT</div>
                <div className="text-[11px] text-slate-500">{new Date(o.created_at).toLocaleDateString("fr-FR")}{o.supplier_invoice ? " · Facture déposée" : ""}</div>
              </button>
            ))}
          </div>
          <div className="col-span-12 lg:col-span-7">
            {sel ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4" data-testid="partner-order-detail">
                <div className="flex items-center justify-between"><div className="font-mono text-slate-300 text-sm">{sel.po_id}</div><div className="text-white font-black">{sel.total_ht_eur?.toLocaleString("fr-FR")} € HT</div></div>
                <table className="w-full text-xs">
                  <thead><tr className="text-slate-500 text-left"><th className="py-1">Produit</th><th>Réf.</th><th className="text-right">Qté</th><th className="text-right">PU HT</th></tr></thead>
                  <tbody>{(sel.lines || []).map((l, i) => <tr key={i} className="border-t border-white/5 text-slate-200"><td className="py-1.5">{l.product_name}</td><td className="text-slate-500">{l.sku}</td><td className="text-right">{l.qty}</td><td className="text-right">{l.unit_cost_eur} €</td></tr>)}</tbody>
                </table>
                {sel.notes && <div className="text-xs text-slate-400">Note : {sel.notes}</div>}
                {(sel.supplier_updates || []).length > 0 && (
                  <div className="text-[11px] text-slate-400 space-y-1">
                    {sel.supplier_updates.map((u, i) => <div key={i}>• {LABEL[u.status]} le {new Date(u.at).toLocaleString("fr-FR")}{u.tracking ? ` · suivi ${u.tracking}` : ""}{u.comment ? ` — ${u.comment}` : ""}</div>)}
                  </div>
                )}
                {next ? (
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    {next.k === "shipped" && <Input placeholder="N° de suivi" value={form.tracking} onChange={(e) => setForm({ ...form, tracking: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-order-tracking" />}
                    <Input placeholder="Commentaire (optionnel)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-order-comment" />
                    <Button onClick={advance} className="font-bold text-[#14101c]" style={{ background: "var(--brand-primary, #f7c935)" }} data-testid={"partner-order-advance-" + next.k}><next.icon className="w-4 h-4 mr-1.5" /> {next.label}</Button>
                  </div>
                ) : <div className="text-xs text-emerald-400 font-bold flex items-center gap-1"><PackageCheck className="w-4 h-4" /> Commande livrée</div>}
                {me.access?.factures && (
                  <div className="border-t border-white/10 pt-4">
                    <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadInvoice(e.target.files?.[0])} data-testid="partner-invoice-input" />
                    {sel.supplier_invoice ? <div className="text-xs text-emerald-300 flex items-center gap-1"><FileText className="w-4 h-4" /> Facture déposée : {sel.supplier_invoice.filename}</div> : null}
                    <Button variant="outline" onClick={() => fileRef.current?.click()} className="border-white/10 text-slate-200 mt-2" data-testid="partner-invoice-btn"><Upload className="w-4 h-4 mr-1.5" /> {sel.supplier_invoice ? "Remplacer la facture" : "Déposer ma facture"}</Button>
                  </div>
                )}
              </div>
            ) : <div className="text-sm text-slate-500 italic p-8 text-center border border-dashed border-white/10 rounded-2xl">Sélectionnez une commande.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function PartnerInvoices() {
  const { axiosAuth } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { axiosAuth().get("/partner/invoices").then((r) => setItems(r.data)).catch(() => setItems([])); /* eslint-disable-next-line */ }, []);
  if (!items) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;
  return (
    <div data-testid="partner-invoices-page">
      <h1 className="text-2xl font-black text-white mb-6">Factures déposées</h1>
      {items.length === 0 ? <div className="text-sm text-slate-500 italic">Aucune facture déposée.</div> : items.map((i) => (
        <div key={i.invoice_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs mb-2" data-testid={"partner-invoice-" + i.invoice_id}>
          <FileText className="w-4 h-4 text-slate-400" /><span className="text-white font-bold flex-1">{i.filename}</span><span className="font-mono text-slate-400">{i.po_id}</span><span className="text-slate-300">{i.amount_ht} € HT</span><span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">{i.status}</span>
        </div>
      ))}
    </div>
  );
}

export function PartnerCatalogue() {
  const { axiosAuth } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { axiosAuth().get("/partner/catalogue").then((r) => setItems(r.data)).catch(() => setItems([])); /* eslint-disable-next-line */ }, []);
  if (!items) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;
  return (
    <div data-testid="partner-catalogue-page">
      <h1 className="text-2xl font-black text-white mb-6">Catalogue</h1>
      {items.length === 0 ? <div className="text-sm text-slate-500 italic">Aucun produit référencé chez vous.</div> : items.map((p) => (
        <div key={p.product_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs mb-2"><span className="font-mono text-slate-500">{p.sku}</span><span className="text-white font-bold flex-1">{p.name}</span><span className="text-slate-300">{p.cost_price_eur} € HT</span></div>
      ))}
    </div>
  );
}
